import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import React, { useContext, useState } from "react";
import { AuthContext } from "../../helpers/AuthContext";
import * as XLSX from 'xlsx';

export const AddDebitMandateModal = ({
  toggleFilter,
  showFilter,
  filterLoading,
  onSubmit // Add this prop to handle submission
}) => {
  const { statuses } = useContext(AuthContext);
  const [display, setDisplay] = useState("input");
  const [mandateCodes, setMandateCodes] = useState([""]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");

  // Handle adding new input field
  const handleAddInput = () => {
    setMandateCodes([...mandateCodes, ""]);
  };

  // Handle removing input field
  const handleRemoveInput = (index) => {
    if (mandateCodes.length > 1) {
      const newCodes = mandateCodes.filter((_, i) => i !== index);
      setMandateCodes(newCodes);
    }
  };

  // Handle input change
  const handleInputChange = (index, value) => {
    const newCodes = [...mandateCodes];
    newCodes[index] = value;
    setMandateCodes(newCodes);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setUploadError("");
    
    if (!file) return;

    // Check if it's an Excel file
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(file.type)) {
      setUploadError("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setUploadedFile(file);
  };

  // Process Excel file and extract mandate codes
  const processExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          // Extract mandate codes from first column, skip header if exists
          const codes = jsonData
            .flat()
            .filter(code => code && typeof code === 'string' && code.trim() !== '')
            .map(code => code.toString().trim());
          
          resolve(codes);
        } catch (error) {
          reject(new Error("Error processing Excel file"));
        }
      };
      reader.onerror = () => reject(new Error("Error reading file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      let mandateCodesArray = [];

      if (display === "input") {
        // Filter out empty codes and trim whitespace
        mandateCodesArray = mandateCodes
          .map(code => code.trim())
          .filter(code => code !== "");
        
        if (mandateCodesArray.length === 0) {
          alert("Please enter at least one mandate code");
          return;
        }
      } else if (display === "upload") {
        if (!uploadedFile) {
          alert("Please upload an Excel file");
          return;
        }
        
        try {
          mandateCodesArray = await processExcelFile(uploadedFile);
          if (mandateCodesArray.length === 0) {
            alert("No valid mandate codes found in the uploaded file");
            return;
          }
        } catch (error) {
          alert("Error processing Excel file: " + error.message);
          return;
        }
      }

      // Prepare data for API
      const requestData = {
        mandateCodes: mandateCodesArray
      };

      // Call the onSubmit prop or your API function
      if (onSubmit) {
    // console.log("requestData: ", requestData);
        await onSubmit(requestData, display);
      }
      
      // Reset form and close modal
      handleReset();
      toggleFilter();
      
    } catch (error) {
      console.error("Submission error:", error);
      alert("An error occurred while submitting. Please try again.");
    }
  };

  // Reset form state
  const handleReset = () => {
    setMandateCodes([""]);
    setUploadedFile(null);
    setUploadError("");
    setDisplay("input");
  };

  // Handle modal close
  const handleClose = () => {
    handleReset();
    toggleFilter();
  };

  return (
    <Modal
      show={showFilter}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Add On Demand Debit Mandate</Modal.Title>
          <span style={{ fontSize: "0.8rem", color: "#475467" }}>
            Select method to add mandate
          </span>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div>
          {/* Tab Navigation */}
          <div style={{ borderBottom: "1px solid lightgray", display: "flex", marginBottom: "20px" }}>
            <div 
              onClick={() => setDisplay("input")} 
              style={{
                borderBottom: display === "input" ? "2px solid #007dcc" : "none", 
                padding: "5px 8px", 
                color: display === "input" ? "#007dcc" : "gray",
                cursor: "pointer"
              }}
            >
              Input
            </div>
            <div 
              onClick={() => setDisplay("upload")} 
              style={{
                borderBottom: display === "upload" ? "2px solid #007dcc" : "none", 
                padding: "5px 8px", 
                color: display === "upload" ? "#007dcc" : "gray",
                cursor: "pointer"
              }}
            >
              Upload
            </div>
          </div>

          {/* Input Mode */}
          {display === "input" && (
            <div>
              <Form>
                {mandateCodes.map((code, index) => (
                  <Row key={index} className="mb-3 align-items-center">
                    <Col md={10}>
                      <Form.Control
                        type="text"
                        placeholder={`Enter mandate code ${index + 1}`}
                        value={code}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                      />
                    </Col>
                    <Col md={2} className="d-flex gap-2">
                      {index === mandateCodes.length - 1 && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleAddInput}
                          title="Add another code"
                        >
                          +
                        </Button>
                      )}
                      {mandateCodes.length > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveInput(index)}
                          title="Remove this code"
                        >
                          ×
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
              </Form>
            </div>
          )}

          {/* Upload Mode */}
          {display === "upload" && (
            <div>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Upload Excel File</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                  />
                  <Form.Text className="text-muted">
                    Please upload an Excel file (.xlsx or .xls) containing mandate codes
                  </Form.Text>
                  {uploadError && (
                    <div className="text-danger mt-2" style={{ fontSize: "0.8rem" }}>
                      {uploadError}
                    </div>
                  )}
                  {uploadedFile && (
                    <div className="text-success mt-2" style={{ fontSize: "0.8rem" }}>
                      File selected: {uploadedFile.name}
                    </div>
                  )}
                </Form.Group>
              </Form>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{ fontSize: "0.8rem" }}
        >
          Cancel
        </Button>
        <Button
          style={{
            backgroundColor: "#007dcc",
            fontSize: "0.8rem",
            fontWeight: 500,
            border: "none"
          }}
          disabled={filterLoading}
          onClick={(e)=>{
            e.preventDefault()
            handleSubmit()
          }}
        >
          {filterLoading ? "Processing..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};